import React from "react";
import { Alert } from 'react-bootstrap'
import { connect } from "react-redux";
import { IAppState, IOffline } from "../state/interfaces";
import QuoteStepLight from "./QuoteStepLightLayout";
import { IRate, IPaymentChoice, ICoverageConfig, ILOBApiClient, IQuoteApiClient, IPaths, IRealTimeAutoResult, IVehicle } from "../classes/Domain";
import ReactLoading from 'react-loading';
import { QUOTE_STEPS, PATHS } from "../AppConstants";
import RateResult from '../components/RateResult';
import Utilities from "../classes/Utilities";
import { IQuoteStepComponentProps, IQuoteStepState } from "../components/interfaces";
import QuoteStepBase from '../components/QuoteStepBase';
import ButtonContinue from "../components/ButtonContinue";
import QuoteActions from "../state/appActions";
import FullQuoteActions from "../state/fullQuoteActions";


interface IState extends IQuoteStepState  {
    offlineData: IOffline | null,
    rateResult: IRate | null,
    selectedRateResult: IRealTimeAutoResult | null | undefined,
    apiCallError: boolean,
    updatingStore: boolean,
    loadingRates: boolean,
    choiceSelected: boolean,
    issueChoice: boolean,
    paymentChoice: IPaymentChoice | null;
    /** Req #7: COMP/COLL deductible override after the alternate-package offer ("0" = LO, "1000" = full). */
    coverageDeductibleOverride: string | null;
}

interface IResultComponentProps extends IQuoteStepComponentProps {
    quoteUid: string,
    coverageConfig: ICoverageConfig,
    vehicles: Array<IVehicle>,
    stateCode: string,
    fillExternalQuote: (update: any) => Promise<any>,
    fillOfflineData: () => Promise<any>,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    PATHS: IPaths;
}

class Result extends QuoteStepBase<IResultComponentProps, IState> {
    constructor(props: IResultComponentProps) {
        super(props);
        this.onPaymentChoiceSelected = this.onPaymentChoiceSelected.bind(this);
        this.continueYes = this.continueYes.bind(this);
        this.continueNo = this.continueNo.bind(this);
        this.onApiCallError = this.onApiCallError.bind(this);

        this.state = {
            offlineData: null,
            rateResult: null,
            selectedRateResult: null,
            apiCallError:false,
            updatingStore:false,
            loadingRates: false,
            choiceSelected: false,
            issueChoice: false,
            paymentChoice: null,
            coverageDeductibleOverride: null
        };
    }

    componentDidMount() 
    {
        super.componentDidMount();
        this.props.reportCurrentStep();
        const loadRates = () => this.getRates();
        this.props.fillOfflineData()
            .then((response: any) => {
                this.setState({ ...this.state, offlineData: (response && response.data) ? response.data : null });
            })
            .catch(() => {
                this.setState({ ...this.state, offlineData: null });
            })
            .then(loadRates, loadRates);
    }

    /** Vehicles as rated: with the alternate-package deductible applied when the customer switched (Req #7). */
    effectiveVehicles(deductibleOverride: string | null): Array<IVehicle> {
        if (deductibleOverride === null) {
            return this.props.vehicles;
        }

        const quoteActions = new QuoteActions(this.props.quoteApiClient);
        return (this.props.vehicles || []).map(v => {
            const baseCoverages = (v.coverages && v.coverages.length)
                ? v.coverages
                : quoteActions.GetQuickVehicleCoverages(this.props.stateCode);
            return {
                ...v,
                coverages: baseCoverages.map(c => (c.code === 'COMP' || c.code === 'COLL')
                    ? { ...c, isSelected: String(c.value) === deductibleOverride }
                    : c)
            } as IVehicle;
        });
    }

    isLiabilityOnly(deductibleOverride: string | null): boolean {
        return !this.effectiveVehicles(deductibleOverride).some(v =>
            (v.coverages || []).some(c => c && c.isSelected && (c.code === 'COMP' || c.code === 'COLL')));
    }

    onAlternateCoverageQuote = () => {
        const nextDeductible = this.isLiabilityOnly(this.state.coverageDeductibleOverride) ? "1000" : "0";
        this.setState({ coverageDeductibleOverride: nextDeductible }, () => {
            this.getRates(nextDeductible);
        });
    };

    getRates(deductibleOverride: string | null = this.state.coverageDeductibleOverride) {
        const coveragesApiClient = this.props.quoteApiClient;
        const quoteActions = new QuoteActions(this.props.quoteApiClient);
        const coverageConfig = quoteActions.syncCoverageConfigFromVehicles(
            this.props.coverageConfig,
            this.effectiveVehicles(deductibleOverride),
            this.props.stateCode);

        // Fresh rate attempt — drop previous "Sorry we can't continue" banner.
        this.props.updateAppState({
            quoteUid: this.props.quoteUid,
            rate: null,
            paymentChoice: null,
            agenciesContactInfo: null,
            rateError: null
        });

        this.setState({ loadingRates: true }, () => {
            coveragesApiClient
                .getRates(this.props.quoteUid, coverageConfig)
                .then((response) => {
                    const anyPaymentChoices = response.data.paymentChoices && 0 < response.data.paymentChoices.length;

                    if (!anyPaymentChoices) {
                        this.props.updateAppState({
                            quoteUid: this.props.quoteUid,
                            rate: null,
                            paymentChoice: null,
                            agenciesContactInfo: response.data.agenciesContactInfo,
                            rateError: response.data.isError ? response.data.msgStatusDesc : null
                        });

                        if (response.data.isError) {
                            this.props.history.goBack();
                        }
                        else {
                            this.props.history.replace(this.props.PATHS.AGENCIES);
                        }

                        return;
                    }

                    let _selectedRateResult = null;
                    let _selectedPaymentChoice: IPaymentChoice | null = null;

                    if (anyPaymentChoices) {
                        _selectedPaymentChoice = (response.data.paymentChoices as Array<IPaymentChoice>)
                            .sort((a, b) => (a.firstPay > b.firstPay) ? 1 : ((b.firstPay > a.firstPay) ? -1 : 0))
                            .sort((a, b) => (a.order > b.order) ? 1 : ((b.order > a.order) ? -1 : 0))[0];
                        _selectedPaymentChoice.selected = true;
                        _selectedRateResult = response.data.rates.find(x => x.program === _selectedPaymentChoice?.program)
                    }

                    this.setState({
                        ...this.state,
                        rateResult: {
                            rates: response.data.rates,
                            paymentChoices: response.data.paymentChoices,
                            agenciesContactInfo: response.data.agenciesContactInfo,
                            isError: response.data.isError,
                            errorCd: response.data.errorCd,
                            msgStatusCd: response.data.msgStatusCd,
                            msgStatusDesc: response.data.msgStatusDesc,
                            offerErrors: response.data.offerErrors
                        },
                        loadingRates: false,
                        paymentChoice: _selectedPaymentChoice,
                        selectedRateResult: _selectedRateResult,
                        choiceSelected: true
                    }, () => {
                            this.props.updateAppState({
                                quoteUid: this.props.quoteUid,
                                rate: this.state.selectedRateResult,
                                paymentChoice: this.state.paymentChoice,
                                agenciesContactInfo: this.state.rateResult?.agenciesContactInfo
                            })
                    });
                },
                () => {
                    this.setState({ ...this.state, loadingRates: false })
                });
        });
    }

    onApiCallError() {
        this.setState({ ...this.state, choiceSelected: false, issueChoice: false });
    }

    /** Buy Now API pipeline (createtQuote → getQuote → fillExternalQuote), then agent-will-call only. */
    continueYes() {
        if (this.state.choiceSelected && null !== this.state.rateResult)
        {
            this.setState({ ...this.state, choiceSelected: true, issueChoice: true }, () => {
                if (null === this.state.rateResult) return;

                const selectedRate = {
                    ...this.state.selectedRateResult, urlMemo: this.state.paymentChoice?.urlMemo ?? "", quoteUid: this.props.quoteUid
                };

                var lobApiClient = this.props.lobApiClient;

                lobApiClient.createtQuote(selectedRate)
                    .then((response) => {
                        const quoteId = parseInt(response.data, 10);

                        lobApiClient.getQuote(quoteId)
                            .then((qResponse) => {
                                qResponse.data.rtrQuoteId = quoteId;
                                qResponse.data.drivers.forEach(x => x.birthDate = '')
                                this.props.fillExternalQuote({ quote: qResponse.data })
                                    .then(() => this.props.history.replace(this.props.PATHS.AGENT_WILL_CALL));
                            });
                    }, this.onApiCallError)
            });
        }
    }

    continueNo() {
        this.props.history.replace(this.props.PATHS.QUOTE_THANK_YOU);
    }

    onPaymentChoiceSelected(paymentChoice: IPaymentChoice) {
        if (!this.state.rateResult) return;

        const wasAlreadySelected = paymentChoice.selected;

        const updatedPaymentChoices = this.state.rateResult.paymentChoices.map(x => {
            const isCurrent = x.program === paymentChoice.program
                && x.payTermCd === paymentChoice.payTermCd
                && x.payPlanDesc === paymentChoice.payPlanDesc
                && x.firstPay === paymentChoice.firstPay
                && x.regularPay === paymentChoice.regularPay
                && x.urlMemo === paymentChoice.urlMemo;

            return {
                ...x,
                selected: isCurrent ? !wasAlreadySelected : false
            };
        });

        const updatedRateResult = {
            ...this.state.rateResult,
            paymentChoices: updatedPaymentChoices
        };

        const hasAnySelection = updatedPaymentChoices.some(x => x.selected);

        const selectedRate = hasAnySelection
            ? updatedRateResult.rates.find(x => x.program === paymentChoice.program)
            : null;

        this.setState({
            rateResult: updatedRateResult,
            selectedRateResult: selectedRate,
            paymentChoice: hasAnySelection ? updatedPaymentChoices.find(x => x.selected) : null,
            choiceSelected: hasAnySelection
        }, () => {
            this.props.updateAppState({
                quoteUid: this.props.quoteUid,
                rate: this.state.selectedRateResult,
                paymentChoice: this.state.paymentChoice,
                agenciesContactInfo: this.state.rateResult?.agenciesContactInfo
            });
        });
    }

    render() {
        const continueDisabled = (this.state.offlineData && this.state.offlineData.isActive)
            || this.state.loadingRates
            || this.state.issueChoice
            || this.state.rateResult?.paymentChoices.every(x => !x.selected);

        return (
            <QuoteStepLight id={this.props.id}>
                <div className="quote-step rates">
                    {this.state.offlineData && this.state.offlineData.isActive && !this.state.loadingRates
                        ?
                        <div>
                            <Alert variant={"warning"}>
                                <p><strong className="text-uppercase">Sorry! Can not process your quote!</strong></p>
                                <p>Please review information below</p>
                                <ul>
                                    <p className="message-body" dangerouslySetInnerHTML={{ __html: this.state.offlineData.message }}></p>
                                </ul>
                            </Alert>
                        </div>
                        :
                        null
                    }

                    {this.state.loadingRates
                        ? <div className={"w-100 loading-indicator"}><ReactLoading type={"spin"} color={"#0096AE"} height={'40px'} width={'40px'} /></div>
                        : null
                    }

                    {!this.state.loadingRates && null !== this.state.rateResult
                        ?
                        <div>
                            <div>
                                {[
                                    this.state.rateResult
                                ].map((rate, idx) => (
                                    <RateResult
                                        key={Utilities.uniqueId()}
                                        rateResult={rate}
                                        currentRate={this.state.selectedRateResult}
                                        index={idx}
                                        onPaymentChoiceSelected={this.onPaymentChoiceSelected}
                                        alternateCoverage={{
                                            isLiabilityOnly: this.isLiabilityOnly(this.state.coverageDeductibleOverride),
                                            onYes: this.onAlternateCoverageQuote,
                                            disabled: this.state.loadingRates
                                        }}
                                        reRate={() => this.getRates()} />
                                ))}
                            </div>
                            <div id="customerQuoteUrl" style={{ display: "none" }}>
                                {window.location.origin + Utilities.formatUrl(PATHS.BUY_NOW_QUOTE + '?tkn={0}', this.state.paymentChoice?.urlMemoTkn)}
                            </div>
                        </div>
                        : null
                    }
                </div>
                <br />
                <div className="choice">
                    {this.state.offlineData && this.state.offlineData.isActive
                        ?
                        null
                        :
                        <div className="text-center">
                            <p className="info">Would you like to continue with the quote?</p>
                            <ButtonContinue
                                disabled={!!continueDisabled}
                                showLoading={this.state.choiceSelected && this.state.issueChoice}
                                label="Yes"
                                onClick={this.continueYes} />
                            {' '}
                            <ButtonContinue
                                disabled={!!continueDisabled || this.state.issueChoice}
                                label="No"
                                onClick={this.continueNo} />
                        </div>
                    }
                </div>
            </QuoteStepLight>
        );
    }
}

let mapProps = (state: IAppState) => {
    return {
        quoteUid: state.quoteUid,
        coverageConfig: state.coverageConfig,
        vehicles: state.vehicles,
        stateCode: state.stateCode
    };
};

let mapDispatch = (dispatch: any, ownProps: IResultComponentProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);
    var fullQuoteActions = new FullQuoteActions(ownProps.lobApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(QUOTE_STEPS.COVERAGES)),
        updateAppState: (update) => dispatch(quoteActions.selectPaymentChoice(update.quoteUid, update.rate, update.paymentChoice, update.agenciesContactInfo, update.rateError)),
        fillExternalQuote: (update) => dispatch(fullQuoteActions.fillExternalQuote(update.quote)),
        fillOfflineData: () => dispatch(quoteActions.fillOfflineData())
    };
};

export default connect(mapProps, mapDispatch)(Result);
